import { apiClient } from './apiClient';
import { FoodDto } from './foods';
import { Allergens } from './ingredients';

export interface MenuDto {
  id: string;
  userId?: string;
  configuration: PreferenceConfigurationDto;
  foods: MenuFoodDto[];
  lastGeneratedDate?: string;
  isDefault: boolean;
  created: string;
  updated?: string;
}

export interface PreferenceConfigurationDto {
  excludedAllergens: Allergens;
  excludedFoodIds: string[];
  excludedIngredientIds: string[];
  excludedCategoryIds: string[];
  selectedSuppliers: SelectedMenuSupplierDto[];
  cadenceDays: number;
  maximumTimeToCook?: number;
  peopleCount: number;
  isCompleted: boolean;
  minimumRating?: number;
  selectedFoodGroupIds: string[];
}

export interface SelectedMenuSupplierDto {
  supplierId: string;
  branchesIds?: string[];
}

export interface MenuFoodDto {
  id: string;
  food: FoodDto;
  day: string;
  discountedProducts: MenuDiscountedProductDto[];
  selected: boolean;
  requireRegenerate: boolean;
}

export interface MenuDiscountedProductDto {
  price?: number;
  productId: string;
  productName: string;
  supplierId: string;
  supplierName: string;
  supplierBranchIds: string[];
  supplierBranchNames: string[];
}

export interface CreateMenuRequest {
  userId?: string;
  excludedAllergens: Allergens;
  selectedSuppliers: SelectedMenuSupplierDto[];
  cadenceDays: number;
  maximumTimeToCook?: number;
  peopleCount: number;
  isCompleted: boolean;
  minimumRating?: number;
  excludedCategoryIds?: string[];
  selectedFoodGroupIds?: string[];
}

export interface UpdateMenuRequest {
  menuId: string;
  excludedAllergens: Allergens;
  selectedSuppliers: SelectedMenuSupplierDto[];
  cadenceDays: number;
  maximumTimeToCook?: number;
  peopleCount: number;
  isCompleted: boolean;
  minimumRating?: number;
  excludedCategoryIds?: string[];
  selectedFoodGroupIds?: string[];
}

export interface AddFoodToMenuRequest {
  menuId: string;
  foodId: string;
  day: string;
  selected: boolean;
}

export interface GenerateSuggestionsRequest {
  menuId: string;
  dates: string[];
}

export interface SelectFoodOfDayRequest {
  menuId: string;
  menuFoodId: string;
  selected: boolean;
}

export async function getMenuById(id: string): Promise<MenuDto> {
  return apiClient.get<MenuDto>(`/api/Menus/${id}`);
}

export async function getMenuByUser(userId: string): Promise<MenuDto> {
  return apiClient.get<MenuDto>(`/api/Menus/user/${userId}`);
}

export async function getDefaultMenu(): Promise<MenuDto | null> {
  return apiClient.get<MenuDto | null>('/api/Menus/default');
}

export async function generateDefaultMenu(days: number = 7): Promise<MenuDto> {
  return apiClient.post<{}, MenuDto>(`/api/Menus/default/generate?days=${days}`, {});
}

export async function createMenu(request: CreateMenuRequest): Promise<MenuDto> {
  return apiClient.post<CreateMenuRequest, MenuDto>('/api/Menus', request);
}

export async function updateMenu(id: string, request: UpdateMenuRequest): Promise<void> {
  return apiClient.put<UpdateMenuRequest, void>(`/api/Menus/${id}`, request);
}

export async function addFoodToMenu(request: AddFoodToMenuRequest): Promise<void> {
  return apiClient.post<AddFoodToMenuRequest, void>(`/api/Menus/${request.menuId}/foods`, request);
}

export async function generateSuggestions(request: GenerateSuggestionsRequest): Promise<void> {
  return apiClient.post<GenerateSuggestionsRequest, void>(`/api/Menus/${request.menuId}/generate-suggestions`, request);
}

export async function addAvailableDates(menuId: string, dates: string[]): Promise<void> {
  return apiClient.post<string[], void>(`/api/Menus/${menuId}/add-dates`, dates);
}

export async function selectFoodOfDay(request: SelectFoodOfDayRequest): Promise<void> {
  return apiClient.post<SelectFoodOfDayRequest, void>(`/api/Menus/${request.menuId}/select-food`, request);
}

export async function regenerateFoodSuggestion(menuId: string, menuFoodId: string, foodGroupIds?: string[]): Promise<void> {
  const params = new URLSearchParams({ menuFoodId });
  (foodGroupIds || []).forEach(id => params.append('foodGroupIds', id));
  return apiClient.post<{}, void>(`/api/Menus/${menuId}/regenerate-food?${params.toString()}`, {});
}

export async function excludeFoodPermanently(menuId: string, menuFoodId: string): Promise<void> {
  return apiClient.post<{}, void>(`/api/Menus/${menuId}/exclude-food?menuFoodId=${menuFoodId}`, {});
}

